// playground — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    background: var(--run-playground-background, var(--run-color-surface-default));
    color: var(--run-playground-color, var(--run-color-text-primary));
    border: var(--run-playground-border-width, 1px) solid var(--run-playground-border-color, var(--run-color-border-default));
    border-radius: var(--run-playground-border-radius, var(--run-radius-semantic-card));
    box-shadow: var(--run-playground-shadow, var(--run-elevation-semantic-raised));
    overflow: hidden;
  }

  [part="container"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-gap, var(--run-space-semantic-xs));
  }

  [part="header"] {
    display: flex;
    align-items: center;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    background: var(--run-playground-toolbar-background, var(--run-color-surface-subtle));
    border-bottom: var(--run-playground-border-width, 1px) solid var(--run-playground-toolbar-border-color, var(--run-color-border-default));
    padding-block: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-toolbar-padding-inline, var(--run-space-semantic-xs));
  }

  .header-tag {
    flex: 1;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    font-weight: 600;
  }

  .main-grid {
    display: grid;
    grid-template-columns: min(320px, var(--run-playground-controls-width, 320px)) 1fr;
    align-items: start;
    gap: var(--run-playground-gap, var(--run-space-semantic-md, 16px));
    padding-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
  }

  :host([mode="full"]) .main-grid {
    grid-template-columns: min(320px, var(--run-playground-controls-width, 320px)) 1fr;
  }

  @media (max-width: 640px) {
    .main-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    :host([mode="full"]) .main-grid {
      grid-template-columns: 1fr;
    }
  }

  [part="preview"] {
    min-height: var(--run-playground-preview-min-height, 200px);
    background: var(--run-playground-preview-background, var(--run-color-surface-default));
    border: var(--run-playground-border-width, 1px) solid var(--run-playground-preview-border-color, var(--run-color-border-subtle));
    border-radius: var(--run-playground-preview-border-radius, var(--run-radius-semantic-card));
    padding-block: var(--run-playground-preview-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-preview-padding-inline, var(--run-space-semantic-xs));
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    /* The preview stays in view while a long controls column scrolls — the
       reference behavior from the yensid_v3 playgrounds (JUNE-139). */
    position: sticky;
    top: var(--run-space-semantic-sm, 12px);
  }

  [part="preview"][aria-busy="true"] {
    opacity: 0.6;
  }

  .preview-host {
    display: contents;
  }

  .preview-error {
    color: var(--run-color-text-primary, inherit);
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    text-align: center;
    padding: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  .preview-loading {
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.5;
  }

  [part="controls"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-gap, var(--run-space-semantic-xs));
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  [aria-label="Component controls"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    flex: 1;
  }

  .control-group {
    display: flex;
    flex-direction: column;
    gap: calc(var(--run-playground-toolbar-gap, var(--run-space-semantic-xs)) / 2);
  }

  .control-label {
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    font-weight: 500;
  }

  .control-description {
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.65;
    margin-block-start: 0;
    margin-block-end: calc(var(--run-playground-toolbar-gap, var(--run-space-semantic-xs)) / 2);
  }

  .control-error-indicator {
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    color: var(--run-color-text-primary, inherit);
    opacity: 0.8;
  }

  .controls-empty {
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.5;
  }

  .reset-row {
    padding-block-start: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    border-top: var(--run-playground-border-width, 1px) solid var(--run-playground-border-color, var(--run-color-border-default));
  }

  [part="code"] {
    background: var(--run-playground-editor-background, var(--run-color-surface-subtle));
    border: var(--run-playground-border-width, 1px) solid var(--run-playground-editor-border-color, var(--run-color-border-subtle));
    border-radius: var(--run-playground-editor-border-radius, var(--run-radius-semantic-card));
    padding-block: var(--run-playground-editor-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-editor-padding-inline, var(--run-space-semantic-xs));
    margin-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
    font-family: var(--run-playground-editor-font-family, var(--run-font-semantic-family-code));
    font-size: var(--run-playground-editor-font-size, var(--run-font-size-sm));
    line-height: var(--run-playground-editor-line-height, var(--run-font-semantic-line-height-heading));
  }

  .auxiliary-tabs {
    margin-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
  }

  .tab-list {
    display: flex;
    border-bottom: var(--run-playground-border-width, 1px) solid var(--run-playground-border-color, var(--run-color-border-default));
    gap: 0;
  }

  .tab-btn {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    color: var(--run-playground-tab-color, var(--run-color-text-secondary));
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    padding-block: var(--run-playground-tab-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-tab-padding-inline, var(--run-space-semantic-xs));
    margin-block-end: -1px;
    transition: color var(--run-playground-transition-duration, var(--run-motion-semantic-standard-duration)) var(--run-playground-transition-easing, var(--run-motion-semantic-standard-easing));
  }

  .tab-btn:hover {
    color: var(--run-playground-tab-color-hover, var(--run-color-text-primary));
  }

  .tab-btn[aria-selected="true"] {
    color: var(--run-playground-tab-color-active, var(--run-color-text-primary));
    background: var(--run-playground-tab-background-active, var(--run-color-surface-default));
    border-bottom-color: var(--run-playground-tab-border-color-active, var(--run-color-border-strong));
  }

  .tab-panel {
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  [part="events"],
  [part="a11y"],
  [part="performance"],
  [part="tokens"] {
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .event-log-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    max-height: 200px;
    overflow-y: auto;
  }

  .event-log-item {
    font-family: var(--run-playground-editor-font-family, var(--run-font-semantic-family-code));
    font-size: var(--run-playground-editor-font-size, var(--run-font-size-sm));
    padding-block: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-toolbar-padding-inline, var(--run-space-semantic-xs));
    background: var(--run-playground-editor-background, var(--run-color-surface-subtle));
    border-radius: var(--run-playground-editor-border-radius, var(--run-radius-semantic-card));
  }

  .event-log-empty {
    opacity: 0.5;
  }

  .panel-coming-soon {
    opacity: 0.5;
    font-style: italic;
  }

  .css-props-panel,
  .slots-panel {
    padding-block-start: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    border-top: var(--run-playground-border-width, 1px) solid var(--run-playground-border-color, var(--run-color-border-default));
    margin-block-start: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .footer-slot {
    padding-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
    padding-block-end: var(--run-playground-padding-block, var(--run-space-semantic-sm));
  }
`;

export function render(host) {
  const {
    tag,
    mode,
    contractData,
    _compoundState,
    _errorMessage,
    _resolvedControls,
    _generatedCode,
    _eventLog,
    _isDirty,
    showCode,
    showEvents,
    showA11y,
    showPerformance,
    showTokenCompliance,
    showCssProps,
    showSlots,
    codeFrameworks,
  } = host;

  const isLoading = _compoundState === 'loading';
  const isError = _compoundState === 'error';
  const controls = Array.isArray(_resolvedControls) ? _resolvedControls : [];
  const eventLog = Array.isArray(_eventLog) ? _eventLog : [];
  const frameworks = Array.isArray(codeFrameworks) ? codeFrameworks : ['html'];
  const activeFramework = frameworks[0] ?? 'html';
  const codeText = (_generatedCode && _generatedCode[activeFramework]) ?? '';

  // ── Header ────────────────────────────────────────────────────────────────
  const badgeHtml = (contractData && contractData.status)
    ? `<run-badge>${contractData.status}</run-badge>`
    : '';

  const headerHtml = `
    <div part="header">
      <slot name="header">
        <span class="header-tag">${tag ? `&lt;${tag}&gt;` : 'run-playground'}</span>
        ${badgeHtml}
        <run-icon-button data-action="share" label="Copy shareable link"></run-icon-button>
      </slot>
    </div>
  `;

  // ── Controls panel ────────────────────────────────────────────────────────
  const controlGroupsHtml = controls.length === 0
    ? `<span class="controls-empty">No controls available.</span>`
    : controls.map((ctrl, i) => {
        const groupId = `ctrl-group-${i}`;
        const labelId = `ctrl-label-${i}`;
        const disabled = isLoading ? 'disabled' : '';
        const errorIndicator = ctrl.hasError
          ? `<span class="control-error-indicator" aria-live="polite">Value error</span>`
          : '';
        // Density (JUNE-139): contract prose lives in the API tables, not the
        // control stack — the description survives as a hover title only.
        const titleAttr = ctrl.description
          ? ` title="${String(ctrl.description).replace(/"/g, '&quot;')}"`
          : '';

        let controlHtml = '';
        if (ctrl.controlType === 'checkbox') {
          controlHtml = `<run-checkbox
            id="ctrl-${i}"
            ${ctrl.currentValue ? 'checked' : ''}
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          ></run-checkbox>`;
        } else if (ctrl.controlType === 'select') {
          const optionsHtml = (ctrl.options ?? []).map(opt =>
            `<span role="option" data-value="${opt}">${opt}</span>`
          ).join('');
          controlHtml = `<run-select
            id="ctrl-${i}"
            value="${ctrl.currentValue ?? ''}"
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          >${optionsHtml}</run-select>`;
        } else {
          const inputType = ctrl.inputType ?? 'text';
          const minAttr = ctrl.min != null ? `min="${ctrl.min}"` : '';
          const maxAttr = ctrl.max != null ? `max="${ctrl.max}"` : '';
          controlHtml = `<run-input
            id="ctrl-${i}"
            type="${inputType}"
            value="${ctrl.currentValue ?? ''}"
            ${minAttr}
            ${maxAttr}
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          ></run-input>`;
        }

        return `
          <div class="control-group" role="group" aria-labelledby="${labelId}" id="${groupId}">
            <span class="control-label" id="${labelId}"${titleAttr}>${ctrl.name}</span>
            ${controlHtml}
            ${errorIndicator}
          </div>
        `;
      }).join('');

  // showCssProps / showSlots are reserved contract knobs (descoped) — no
  // placeholder panels render for them; see contract.md "Reserved panels".

  const controlsPanelHtml = `
    <div part="controls">
      <div aria-label="Component controls">
        ${controlGroupsHtml}
      </div>
      <div class="reset-row">
        <run-button
          data-action="reset"
          size="sm"
          variant="secondary"
          ${!_isDirty || isLoading ? 'disabled' : ''}
        >Reset</run-button>
      </div>
    </div>
  `;

  // ── Preview area ──────────────────────────────────────────────────────────
  const previewInnerHtml = isError
    ? `<div role="alert" class="preview-error">${_errorMessage ?? 'An error occurred.'}</div>`
    : isLoading
      ? `<span class="preview-loading" aria-live="polite">Loading&hellip;</span>`
      : `<div class="preview-host"></div>`;

  const previewHtml = `
    <div part="preview" ${isLoading ? 'aria-busy="true"' : ''}>
      ${previewInnerHtml}
    </div>
  `;

  // ── Main grid ─────────────────────────────────────────────────────────────
  // Structural container is a plain div: run-grid's shadow DOM wraps slotted
  // children in an internal wrapper, so the host-level grid-template-columns
  // never laid out the panes (everything stacked). Controls sit in the left
  // column, the live preview on the right.
  const mainGridHtml = `
    <div class="main-grid">
      ${controlsPanelHtml}
      ${previewHtml}
    </div>
  `;

  // ── Compact mode: code panel below grid ───────────────────────────────────
  const compactCodeHtml = (mode !== 'full' && showCode)
    ? `<div part="code"><run-code-display frameworks='${JSON.stringify(frameworks)}'></run-code-display></div>`
    : '';

  // ── Full mode: auxiliary tabs ─────────────────────────────────────────────
  // Only panels with real implementations get tabs. The contract's a11y /
  // performance / token-compliance panels are reserved (descoped) — rendering
  // a "coming soon" placeholder is the hollow-surface defect this component
  // is recovering from (JUNE-115 / JUNE-139).
  let auxiliaryTabsHtml = '';
  if (mode === 'full') {
    const tabs = [];
    if (showCode)   tabs.push({ key: 'code',   label: 'Code' });
    if (showEvents) tabs.push({ key: 'events', label: 'Events' });

    if (tabs.length > 0) {
      const requestedKey = host._activeAuxTab;
      const activeKey = tabs.some(t => t.key === requestedKey) ? requestedKey : tabs[0].key;

      const tabButtonsHtml = tabs.map(t =>
        `<button
          class="tab-btn"
          role="tab"
          aria-selected="${t.key === activeKey ? 'true' : 'false'}"
          aria-controls="tabpanel-${t.key}"
          id="tab-${t.key}"
          tabindex="${t.key === activeKey ? '0' : '-1'}"
          data-tab="${t.key}"
        >${t.label}</button>`
      ).join('');

      const tabPanelsHtml = tabs.map(t => {
        let panelContent = '';
        if (t.key === 'code') {
          panelContent = `<div part="code"><run-code-display frameworks='${JSON.stringify(frameworks)}'></run-code-display></div>`;
        } else if (t.key === 'events') {
          const entriesHtml = eventLog.length === 0
            ? `<span class="event-log-empty">No events captured yet.</span>`
            : `<ul class="event-log-list">${eventLog.map(ev =>
                `<li class="event-log-item"><strong>${ev.eventName}</strong> — ${new Date(ev.timestamp).toLocaleTimeString()}</li>`
              ).join('')}</ul>`;
          panelContent = `
            <div part="events">
              <div data-event-log>${entriesHtml}</div>
              <run-button data-action="clear-events" size="sm" variant="secondary">Clear event log</run-button>
            </div>
          `;
        }

        return `
          <div
            class="tab-panel"
            role="tabpanel"
            id="tabpanel-${t.key}"
            aria-labelledby="tab-${t.key}"
            ${t.key !== activeKey ? 'hidden' : ''}
          >${panelContent}</div>
        `;
      }).join('');

      auxiliaryTabsHtml = `
        <div class="auxiliary-tabs">
          <div class="tab-list" role="tablist" aria-label="Playground panels">
            ${tabButtonsHtml}
          </div>
          ${tabPanelsHtml}
        </div>
      `;
    }
  }

  // ── Live regions (visually hidden, managed by base — skin must render them) ─
  const liveRegionsHtml = `
    <div class="visually-hidden" aria-live="polite" aria-atomic="true" data-announce-polite></div>
    <div class="visually-hidden" role="alert" data-announce-assertive></div>
  `;

  // ── Footer slot ───────────────────────────────────────────────────────────
  const footerHtml = `<div class="footer-slot"><slot name="footer"></slot></div>`;

  // ── Assemble ──────────────────────────────────────────────────────────────
  host.shadowRoot.innerHTML = `
    <div part="container">
      ${liveRegionsHtml}
      ${headerHtml}
      ${mainGridHtml}
      ${compactCodeHtml}
      ${auxiliaryTabsHtml}
      ${footerHtml}
    </div>
  `;

  // innerHTML cannot set JS properties — `.code="..."` in a template string
  // creates a literal attribute named ".code", not a property binding.
  // Set the code property imperatively now that the elements exist in the DOM.
  if (codeText) {
    host.shadowRoot.querySelectorAll('run-code-display').forEach(cd => {
      cd.code = codeText;
    });
  }
}